import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FOUNDER_PORTRAIT } from "@/lib/constants";

const pillars = [
  { title: "Diagnose", body: "Identify the student, leadership or institutional need with rigor." },
  { title: "Decide", body: "Translate information into the most strategic next step." },
  { title: "Design", body: "Build the right program, curriculum, system or plan." },
  { title: "Deliver", body: "Put it into practice in a way that produces measurable value." },
];

const credentials = [
  "25+ years in education, finance & economics",
  "Doctorate in finance and economics",
  "Chief Economist, Womenomic$ Magazine",
  "Bestselling author — The Money Shot and The Money Makeover",
  "Speaking since 1999 — Carnegie Hall to campus assemblies",
  "Taught at Central State University",
  "Built online business & finance programs for four universities including Kaplan",
];

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="overline">About EdgeTech Global</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] mt-4 max-w-4xl">
            We help institutions know <em className="italic text-[#0F4C5C]">what to do next.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#22223B]/80 leading-relaxed max-w-3xl">
            EdgeTech Global is an education technology and strategic educational design company. We sit at the intersection of education, decision intelligence, leadership development, curriculum innovation and applied AI.
          </p>
        </div>
      </section>

      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline">Our Belief</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              Schools are drowning in <em className="italic text-[#0F4C5C]">information</em> — starving for clarity.
            </h2>
          </div>
          <div className="md:col-span-7 text-lg leading-relaxed text-[#22223B]/80 space-y-6">
            <p>
              Schools already collect data, administer assessments, purchase technology and implement programs. The problem is rarely a lack of information. The problem is knowing what the information means, which decisions should follow, and how to translate those decisions into action.
            </p>
            <p>
              EdgeTech Global creates the structure that connects information to better decisions.
            </p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#0F4C5C] text-[#FDFBF7] grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline text-[#E3B23C]">The framework</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
            Diagnose. Decide. Design. Deliver.
          </h2>
          <div className="grid md:grid-cols-4 gap-4 mt-12">
            {pillars.map((p, i) => (
              <div key={p.title} className="p-8 border border-white/10 rounded-lg bg-white/[0.03]">
                <div className="overline text-[#FDFBF7]/50">0{i + 1}</div>
                <h3 className="font-serif-display text-2xl mt-4">{p.title}</h3>
                <p className="mt-3 text-sm text-[#FDFBF7]/75 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-lg overflow-hidden bg-[#F0EDE6]">
              <img src={FOUNDER_PORTRAIT} alt="Dr. Crystal Gifford" className="w-full h-full object-cover object-top" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="overline">Founder</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              Dr. Crystal Gifford
            </h2>
            <p className="mt-6 text-lg text-[#22223B]/80 leading-relaxed">
              Dr. Crystal Gifford — <em className="text-[#0F4C5C]">"The Conscious Economist"</em> — holds a doctorate in finance and economics with 25+ years as an entrepreneur, educator and speaker. She has taught at Central State University and built online business and finance programs for four universities (including Kaplan), is Chief Economist of Womenomic$ Magazine, and is the bestselling author of <em>The Money Shot</em> and <em>The Money Makeover.</em> Speaking since 1999, she has energized audiences from intimate rooms to thousands — from private-school campuses to the Carnegie Hall stage. Her throughline is simple: <strong className="text-[#0F4C5C]">help people make great decisions faster.</strong>
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-y-3 gap-x-8">
              {credentials.map((c) => (
                <li key={c} className="text-[#22223B]/85 flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#E3B23C]" /> {c}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/speaking" data-testid="about-speaking-cta" className="btn-primary text-sm">
                Bring Dr. Crystal to Your Organization <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PERSONAL NOTE */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div className="overline text-[#0F4C5C]">A personal note</div>
          <div className="mt-8 relative">
            <span className="absolute -top-6 -left-2 font-serif-display italic text-8xl text-[#E3B23C]/40 leading-none select-none">&ldquo;</span>
            <div className="font-serif-display text-2xl md:text-3xl leading-snug text-[#22223B] space-y-6 relative">
              <p>
                I built EdgeTech Global because I've spent 25 years watching brilliant schools spend their <em className="italic text-[#0F4C5C]">time, energy, attention and money</em> on defaults nobody actually chose.
              </p>
              <p>
                Kids preparing for the wrong test. Leaders making the same decision over and over without a framework. Curriculum bought against a hypothesis instead of a diagnostic. And underneath all of it, a quiet toll on the people doing the work.
              </p>
              <p>
                We don't have to run our schools that way. When schools decide before they design, everything downstream — the programs, the conversations with families, the way a Tuesday afternoon actually feels — gets clearer, cheaper, and kinder.
              </p>
              <p>
                That's the work. Come build it with us.
              </p>
            </div>
            <div className="mt-10 pt-8 border-t border-[#0F4C5C]/15 flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="font-serif-display italic text-3xl md:text-4xl text-[#0F4C5C]">
                  — Dr. Crystal
                </div>
                <div className="text-sm text-[#6B705C] mt-1">Founder &amp; CEO, EdgeTech Global</div>
              </div>
              <div className="font-serif-display italic text-2xl md:text-3xl text-[#E3B23C]">
                Live Adventurously.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
